import './FallbackTrackingStyles.css';
import BlockUi from '@availity/block-ui';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { connect } from 'react-redux';
import { DateInput } from 'semantic-ui-calendar-react-17';
import { Button, DropdownProps, Form, Header, Icon, InputOnChangeData, Message, Segment } from 'semantic-ui-react';
import { AgencyStateType } from '../../../actions/actionTypes';
import { getFallbackEpisodes, getFallbackTrack } from '../../../actions/delaysAnalysisActions';
import { getRoutes, getShapeData } from '../../../actions/gtfsStaticActions';
import Api from '../../../api/api';
import { AppState } from '../../../reducers';
import { getSelectedOrDefaultAgency } from '../../../selectors';
import { FallbackEpisode, VehicleFallbackTrack } from '../../../types/fallbackTrackingTypes';
import { InfoboxProps, PolylineProps, PushpinProps } from '../../../types/MapModels';
import { DropDownStateType } from '../../../types/types';
import BusHistorySlider from '../../bushistory/BusHistorySlider';
import TransitMap from '../../shared/AzureMap/TransitMap';
import { createVehicleMapElements } from '../../shared/VehicleMapElements';
import FallbackEpisodesLegend from './FallbackEpisodesLegend';
import FallbackEpisodesTable from './FallbackEpisodesTable';
import FallbackTrackingLegend from './FallbackTrackingLegend';
import { describeTrackPoint, FallbackEpisodesReportHeaders, toFallbackEpisodesReport, TrackPointView } from './fallbackTrackingReport';
import { SHAPE_STYLE, toSegmentPolyline } from './fallbackTrackingStyles';

/**
 * Default minimum gap, same as the API default: vehicles report every 5 s and shorter gaps are GPS jitter, not signal loss.
 */
const DEFAULT_MIN_GAP_SEC = 60;

interface Props {
    agency: AgencyStateType | undefined;
}

type MapBounds = { north: number; south: number; east: number; west: number; };

const initialDropDownState: DropDownStateType = {
    options: [],
    selectedValue: '',
};

const FallbackTrackingForm: React.FC<Props> = ({ agency }) => {
    const [blockingState, setBlockingState] = useState(false);
    const [center, setCenter] = useState<CoordinatePair>();
    const [selectedDateState, setSelectedDateState] = useState(moment().add(-1, 'days').format('YYYY-MM-DD'));
    const [routesState, setRoutesState] = useState<DropDownStateType>(initialDropDownState);
    const [vehicleIdState, setVehicleIdState] = useState('');
    const [minGapSecState, setMinGapSecState] = useState(DEFAULT_MIN_GAP_SEC);
    const [episodesState, setEpisodesState] = useState<FallbackEpisode[]>();
    const [selectedEpisodeState, setSelectedEpisodeState] = useState<FallbackEpisode>();
    const [trackState, setTrackState] = useState<VehicleFallbackTrack>();
    const [shapePolylineState, setShapePolylineState] = useState<PolylineProps>();
    const [mapBoundsState, setMapBoundsState] = useState<MapBounds>();
    const [sliderValueState, setSliderValueState] = useState(0);

    useEffect(() => {
        if (!agency) return;
        (async (agencyId: string) => {
            const { Latitude, Longitude } = await Api.getOrgLocation(agencyId);
            setCenter([Latitude, Longitude]);
        })(agency.id);
    }, [agency?.id]);

    useEffect(() => {
        clearResults();
        if (!agency) return;
        (async (agencyId: string) => {
            const routes = await getRoutes(agencyId, selectedDateState);
            setRoutesState({
                options: routes.map(r => ({ value: r.routeName, text: r.routeName })),
                selectedValue: '',
            });
        })(agency.id);
    }, [agency?.id, selectedDateState]);

    const clearResults = () => {
        setEpisodesState(undefined);
        setSelectedEpisodeState(undefined);
        setTrackState(undefined);
        setShapePolylineState(undefined);
        setMapBoundsState(undefined);
        setSliderValueState(0);
    };

    const handleDateChange = (_event: React.SyntheticEvent<HTMLElement>, { value }: { value: string; }) => {
        setSelectedDateState(value);
    };

    const handleRouteChange = (_event: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
        setRoutesState(prevState => ({ ...prevState, selectedValue: value as string }));
    };

    const handleMinGapChange = (_event: React.ChangeEvent<HTMLInputElement>, { value }: InputOnChangeData) => {
        setMinGapSecState(Number(value));
    };

    const handleSearch = async () => {
        if (!agency) return;
        clearResults();
        setBlockingState(true);
        try {
            const episodes = await getFallbackEpisodes({
                agencyId: agency.id,
                serviceDate: selectedDateState,
                routeShortName: (routesState.selectedValue as string) || undefined,
                vehicleId: vehicleIdState.trim() || undefined,
                minGapSec: minGapSecState,
            });
            setEpisodesState(episodes);
        } finally {
            setBlockingState(false);
        }
    };

    const handleEpisodeSelect = useCallback(async (episode: FallbackEpisode) => {
        if (!agency) return;
        setSelectedEpisodeState(episode);
        setBlockingState(true);
        try {
            const track = await getFallbackTrack({
                agencyId: agency.id,
                serviceDate: selectedDateState,
                tripId: episode.tripId,
                vehicleId: episode.vehicleId,
                minGapSec: minGapSecState,
            });
            setTrackState(track);
            const shapeId = track?.shapeId || episode.shapeId;
            const shapePoints = shapeId ? await getShapeData(agency.id, selectedDateState, shapeId) : [];
            if (shapePoints.length > 0) {
                setShapePolylineState({
                    locations: shapePoints.map(({ latitude, longitude }) => [latitude, longitude]),
                    options: SHAPE_STYLE,
                });
            }
            const trackPoints = track?.segments.flatMap(s => s.points) ?? [];
            const boundsPoints = shapePoints.length > 0 ? shapePoints : trackPoints;
            if (boundsPoints.length > 0) {
                const lats = boundsPoints.map(p => p.latitude);
                const lons = boundsPoints.map(p => p.longitude);
                setMapBoundsState({ north: Math.max(...lats), south: Math.min(...lats), east: Math.max(...lons), west: Math.min(...lons) });
            }
            const episodeStart = moment.parseZone(episode.start).valueOf();
            const firstEpisodePoint = (track?.segments ?? [])
                .flatMap(s => s.points)
                .findIndex(p => moment.parseZone(p.time).valueOf() >= episodeStart);
            setSliderValueState(Math.max(firstEpisodePoint, 0));
        } finally {
            setBlockingState(false);
        }
    }, [agency, selectedDateState, minGapSecState]);

    const sliderPoints = useMemo<TrackPointView[]>(() =>
        (trackState?.segments ?? []).flatMap(segment =>
            segment.points.map((point, index) => ({
                ...point,
                kind: segment.kind,
                recoveryOffsetMeters: index === segment.points.length - 1 ? segment.recoveryOffsetMeters : undefined,
            }))),
    [trackState]);

    const episodesReport = useMemo(() => toFallbackEpisodesReport(episodesState ?? []), [episodesState]);

    // Stable map props: TransitMap compares them by value on every render, so the slider must not rebuild them.
    const mapProps = useMemo(() => ({ center, options: { bounds: mapBoundsState } }), [center, mapBoundsState]);

    const polylines = useMemo<PolylineProps[]>(() => [
        ...(shapePolylineState ? [shapePolylineState] : []),
        ...(trackState?.segments ?? []).map(toSegmentPolyline),
    ], [shapePolylineState, trackState]);

    const currentPoint = sliderPoints[sliderValueState];

    const vehicleElements = useMemo(() => {
        if (!currentPoint || !selectedEpisodeState) return undefined;
        return createVehicleMapElements({
            location: [currentPoint.latitude, currentPoint.longitude],
            vehicleId: selectedEpisodeState.vehicleId,
            description: describeTrackPoint(currentPoint),
        });
    }, [currentPoint, selectedEpisodeState]);

    const vehiclePushpins: PushpinProps[] = vehicleElements ? [vehicleElements.pushpin] : [];
    const vehicleInfoboxes: InfoboxProps[] = vehicleElements ? [vehicleElements.infobox] : [];

    return (
        <BlockUi tag="div" blocking={blockingState} className="fallback-tracking">
            <Header as="h1" className="reportHeader">
                Fallback Tracking
            </Header>
            <Message info className="fallback-tracking-about">
                <Message.Header>About this page</Message.Header>
                <p>
                    Shows the vehicles that stopped reporting positions while the GTFS-RT feed kept publishing their predictions
                    (retained predictions fallback), and the tracking consumers saw during that time.
                </p>
                <Message.List>
                    <Message.Item>
                        A fallback episode is a gap between consecutive positions of the same vehicle and trip of at least the
                        minimum gap. The default is 60 s: vehicles report every 5 s, so shorter gaps are GPS jitter, not signal loss.
                    </Message.Item>
                    <Message.Item>
                        During the gap the feed keeps the delay observed at the last position. The synthetic tracking starts where
                        the vehicle was last seen and advances at the scheduled pace until the retention window ends (600 s by default).
                    </Message.Item>
                    <Message.Item>
                        The episode is Recovered when the vehicle reports again inside the window; otherwise it is Expired.
                    </Message.Item>
                    <Message.Item>
                        Delay error compares the delay reported when the vehicle came back with the kept delay. On the map, where red
                        and green overlap, the fallback placed the vehicle further than it actually was.
                    </Message.Item>
                    <Message.Item>
                        Select a date and optional filters, search, then click an episode to see its trip tracking on the map.
                    </Message.Item>
                </Message.List>
            </Message>
            <Form onSubmit={handleSearch}>
                <Form.Group inline className="params inputGroup">
                    <DateInput
                        className="date"
                        name="serviceDate"
                        fluid
                        dateFormat="YYYY-MM-DD"
                        placeholder="Select date"
                        value={selectedDateState}
                        iconPosition="left"
                        popupPosition="bottom center"
                        closable={true}
                        animation="fade"
                        onChange={handleDateChange}
                        maxDate={new Date()}
                    />
                    <Form.Select
                        placeholder="All routes"
                        fluid
                        search
                        selection
                        clearable
                        openOnFocus={false}
                        selectOnBlur={false}
                        options={routesState.options}
                        value={routesState.selectedValue}
                        onChange={handleRouteChange}
                    />
                    <Form.Input
                        placeholder="All vehicles"
                        value={vehicleIdState}
                        onChange={(_e, { value }) => setVehicleIdState(value)}
                    />
                    <Form.Input
                        label="Min gap (s)"
                        type="number"
                        min={1}
                        value={minGapSecState}
                        onChange={handleMinGapChange}
                        title="Minimum time without positions considered a fallback. Default: 60 s."
                    />
                    <Form.Field className="commands">
                        <Button primary type="submit" disabled={!agency || !(minGapSecState > 0)}>
                            Search
                        </Button>
                        {episodesState && episodesState.length > 0 &&
                            <CSVLink
                                headers={FallbackEpisodesReportHeaders}
                                data={episodesReport}
                                filename={`FallbackEpisodes-${selectedDateState}.csv`}
                                target="_blank"
                                className="ui button basic"
                            >
                                <Icon name="download" />
                                CSV
                            </CSVLink>}
                    </Form.Field>
                </Form.Group>
            </Form>
            <div className="fallback-tracking-content">
                <Segment className="episodes-pane">
                    {episodesState === undefined && <Message info content="Select a date and search to list fallback episodes." />}
                    {episodesState?.length === 0 && <Message content="No fallback episodes found for the selected filters." />}
                    {episodesState && episodesState.length > 0 &&
                        <>
                            <div className="episodes-scroll">
                                <FallbackEpisodesTable
                                    episodes={episodesState}
                                    selectedEpisode={selectedEpisodeState}
                                    onSelect={handleEpisodeSelect}
                                />
                            </div>
                            <FallbackEpisodesLegend />
                        </>}
                </Segment>
                <Segment className="map-pane">
                    <div className="map-container">
                        <TransitMap
                            map={mapProps}
                            polylines={polylines}
                            pushpins={vehiclePushpins}
                            infoboxes={vehicleInfoboxes}
                        />
                    </div>
                    {sliderPoints.length > 0 &&
                        <div className="slider">
                            <BusHistorySlider
                                maxValue={sliderPoints.length - 1}
                                sliderValue={sliderValueState}
                                handleSliderChange={setSliderValueState}
                            />
                        </div>}
                    <FallbackTrackingLegend />
                </Segment>
            </div>
        </BlockUi>
    );
};

export default connect(
    (state: AppState) => ({ agency: getSelectedOrDefaultAgency(state) }),
)(FallbackTrackingForm);
